import { QueryObserverResult, useMutation, UseMutationResult, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

import { APPLICATION_USERS_RESTAURANTS_UNKNOWN_ITEMS_QUERY_KEY } from '../api/apiConsts';
import {
  assignRelevos,
  assignUnknownRelevos,
  getUnknownRelevosToReturn,
  returnRelevos,
  returnUnknownRelevos,
} from '../api/relevos';
import { AssignedRelevosResponse, AssignUnknownItemsResponse, CategoryQuantity } from '../api/types';
import { HttpException } from '../common/models/httpException';
import { useTranslations } from '../contexts/LocalizationContext';
import { QuickContactType } from '../modules/QuickContacts/model/enums/quick-contact-type.enum';
import { ReturnUnknownRelevosRequest } from '../modules/RelevoAssignment/model/return-unknown-relevos-request';
import { ReturnUnknownRelevosResponse } from '../modules/RelevoAssignment/model/return-unknown-relevos-response';
import { UnknownRelevosListResponse } from '../modules/RelevoAssignment/model/unknown-relevos-list-response';

const getUnknownItemsToReturnQueryKey = (restaurantId: string) => [
  APPLICATION_USERS_RESTAURANTS_UNKNOWN_ITEMS_QUERY_KEY,
  restaurantId,
];

export const useUnknownItemsToReturn = (
  restaurantId: string,
): QueryObserverResult<UnknownRelevosListResponse[], AxiosError> => {
  return useQuery(getUnknownItemsToReturnQueryKey(restaurantId), () => getUnknownRelevosToReturn(restaurantId), {
    refetchOnWindowFocus: false,
    cacheTime: 0,
  });
};

export const useAssignUnknownItems = (): UseMutationResult<
  AssignUnknownItemsResponse,
  AxiosError<HttpException>,
  { userId: string; restaurantId: string; request: CategoryQuantity }
> => {
  return useMutation(
    ({ userId, restaurantId, request }: { userId: string; restaurantId: string; request: CategoryQuantity }) =>
      assignUnknownRelevos(userId, request, restaurantId),
  );
};

export const useAssignItems = (): UseMutationResult<
  AssignedRelevosResponse,
  AxiosError<HttpException>,
  {
    userId: string;
    relevos: string[];
    restaurantId: string;
    quickContactType?: QuickContactType;
  }
> => {
  return useMutation(
    ({
      userId,
      restaurantId,
      relevos,
      quickContactType,
    }: {
      userId: string;
      relevos: string[];
      restaurantId: string;
      quickContactType?: QuickContactType;
    }) => assignRelevos(userId, relevos, restaurantId, quickContactType),
  );
};

export const useReturnItems = (): UseMutationResult<
  AssignedRelevosResponse,
  AxiosError<HttpException>,
  { restaurantId: string; relevos: string[] }
> => {
  const translations = useTranslations();
  return useMutation(
    ({ restaurantId, relevos }: { restaurantId: string; relevos: string[] }) => returnRelevos(relevos, restaurantId),
    {
      onError: (error) => {
        toast.error(
          error.response?.data.message || error.response?.data?.error || translations('something_went_wrong'),
        );
      },
    },
  );
};

export const useReturnUnknownItems = (): UseMutationResult<
  ReturnUnknownRelevosResponse,
  AxiosError<HttpException>,
  { restaurantId: string; request: ReturnUnknownRelevosRequest }
> => {
  const queryClient = useQueryClient();
  const translations = useTranslations();
  return useMutation(
    ({ restaurantId, request }: { restaurantId: string; request: ReturnUnknownRelevosRequest }) =>
      returnUnknownRelevos(restaurantId, request),
    {
      onError: (error) => {
        toast.error(error.response?.data.message || error.response?.data.error || translations('something_went_wrong')); // todo: remove error.response?.data.error once translations are added on the backend side
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(APPLICATION_USERS_RESTAURANTS_UNKNOWN_ITEMS_QUERY_KEY);
      },
    },
  );
};
